/**
 * StockHomologationDialog - Nova homologação de um rastreador (BR-EST-030..032).
 *
 * Etapas na mesma tela: buscar rastreador (IMEI/ESN) → resultado → observação.
 * Tipo fixo em "Alterar Status do Rastreador" até a parte de Configuração ser implementada.
 * Opções bloqueadas pela situação atual aparecem desabilitadas com o motivo.
 * A validação definitiva é feita em `create_stock_homologation`.
 *
 * RELEASE: 2026-10-11 v1.2.0 - Aprovado exige plataforma homologada (somente ativas).
 * RELEASE: 2026-10-11 v1.1.0 - Remove seleção de tipo (fixo em Alterar Status) até Configuração.
 * RELEASE: 2026-10-11 v1.0.0 - Versão inicial.
 */
import { useEffect, useMemo, useState } from 'react';
import { BadgeCheck, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useStockItemsList, type StockItemRow } from '@/hooks/useStockItems';
import { useCreateStockHomologation } from '@/hooks/useStockHomologations';
import {
  HOMOLOGATION_RULES, evaluateHomologation, homologationRequiresNotes, homologationRequiresPlatform,
  type HomologationResult, type HomologationType,
} from '@/lib/stock-homologation';
import { stockStatusLabel } from '@/lib/stock-presentation';
import { cn } from '@/lib/utils';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { useTrackingPlatforms } from '@/hooks/useTrackingPlatforms';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rastreador pré-selecionado (ex.: aberto pela ficha) */
  initialTracker?: StockItemRow | null;
}

export function StockHomologationDialog({ open, onOpenChange, initialTracker }: Props) {
  const create = useCreateStockHomologation();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [tracker, setTracker] = useState<StockItemRow | null>(null);
  const [type, setType] = useState<HomologationType>('status_change');
  const [result, setResult] = useState<HomologationResult | null>(null);
  const [notes, setNotes] = useState('');
  const [platformId, setPlatformId] = useState<string | null>(null);
  // Plataformas ativas para o resultado Aprovado
  const platforms = useTrackingPlatforms({ search: '', status: 'active', page: 1, pageSize: 100 }, open);

  // Reinicia o formulário a cada abertura
  useEffect(() => {
    if (!open) return;
    setSearch(''); setDebounced(''); setTracker(initialTracker ?? null);
    setType('status_change'); setResult(null); setNotes(''); setPlatformId(null);
  }, [open, initialTracker]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isFetching } = useStockItemsList('tracker', {
    search: debounced, status: 'all', kit: 'all', active: 'active', page: 1, pageSize: 8,
  });
  const candidates = debounced.length >= 3 && !tracker ? (data?.items ?? []) : [];

  const evaluation = useMemo(
    () => (tracker && result ? evaluateHomologation(type, result, tracker.status, !!tracker.kit_id) : null),
    [tracker, type, result],
  );
  const notesMissing = !!result && homologationRequiresNotes(result) && !notes.trim();
  const platformMissing = homologationRequiresPlatform(result) && !platformId;
  const canSubmit = !!tracker && !!result && evaluation?.ok === true && !notesMissing && !platformMissing && !create.isPending;

  const submit = () => {
    if (!tracker || !result) return;
    create.mutate({ trackerId: tracker.id, type, result, notes, platformId: homologationRequiresPlatform(result) ? platformId : null }, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !create.isPending && onOpenChange(v)}>
      <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
        <PremiumDialogHeader icon={BadgeCheck} title="Nova homologação" />
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* 1. Rastreador */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Rastreador</Label>
            {tracker ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                <div className="min-w-0">
                  <p className="font-mono text-sm font-medium">{tracker.imei}</p>
                  <p className="text-xs text-muted-foreground">{tracker.model_name ?? 'Sem modelo'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{stockStatusLabel('tracker', tracker.status)}</Badge>
                  {!initialTracker && (
                    <Button variant="ghost" size="sm" onClick={() => { setTracker(null); setResult(null); }}>Trocar</Button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input className="pl-9" placeholder="Buscar por IMEI ou ESN (mín. 3 caracteres)" value={search}
                    maxLength={30} onChange={(e) => setSearch(e.target.value.replace(/[^0-9a-zA-Z]/g, ''))} />
                  {isFetching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
                </div>
                {candidates.length > 0 && (
                  <div className="rounded-lg border divide-y">
                    {candidates.map((c) => (
                      <button key={c.id} type="button" onClick={() => setTracker(c)}
                        className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-muted/50">
                        <span className="font-mono text-sm">{c.imei}</span>
                        <span className="text-xs text-muted-foreground">{stockStatusLabel('tracker', c.status)}</span>
                      </button>
                    ))}
                  </div>
                )}
                {debounced.length >= 3 && !isFetching && candidates.length === 0 && (
                  <p className="text-xs text-muted-foreground">Nenhum rastreador encontrado.</p>
                )}
              </>
            )}
          </div>

          {/* 2. Resultado */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Resultado</Label>
            <div className="space-y-1.5">
              {HOMOLOGATION_RULES[type].map((r) => {
                const ev = tracker ? evaluateHomologation(type, r.result, tracker.status, !!tracker.kit_id) : null;
                const blocked = ev?.ok === false;
                return (
                  <button key={r.result} type="button" disabled={!tracker || blocked}
                    onClick={() => setResult(r.result)}
                    className={cn('flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm',
                      result === r.result ? 'border-primary bg-primary/5' : 'hover:bg-muted/40',
                      (!tracker || blocked) && 'cursor-not-allowed opacity-50')}>
                    <span className="font-medium">{r.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {blocked ? ev.reason : r.target ? `→ ${stockStatusLabel('tracker', r.target)}` : 'Não altera a situação'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 3. Plataforma homologada (obrigatória no Aprovado) */}
          {homologationRequiresPlatform(result) && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Plataforma homologada (obrigatória)</Label>
              <SearchableSelect value={platformId} onChange={setPlatformId} isLoading={platforms.isLoading}
                placeholder="Selecione a plataforma"
                options={(platforms.data?.items ?? []).map((p) => ({ value: p.id, label: p.name, hint: p.supplier_name }))} />
              {!platforms.isLoading && (platforms.data?.items.length ?? 0) === 0 && (
                <p className="text-xs text-muted-foreground">Nenhuma plataforma ativa. Cadastre em Gestão Rastreador › Plataformas.</p>
              )}
            </div>
          )}

          {/* 4. Observação */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">
              Observação {result && homologationRequiresNotes(result) ? '(obrigatória)' : '(opcional)'}
            </Label>
            <Textarea rows={3} maxLength={1000} value={notes}
              onChange={(e) => setNotes(e.target.value.replace(/[<>]/g, '').slice(0, 1000))}
              placeholder="Descreva os testes realizados..." />
          </div>
        </div>
        <DialogFooter className="border-t bg-muted/30 px-6 py-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>Cancelar</Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {create.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Finalizar homologação
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
